/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, Checkbox, DefaultButton, Icon, Link, PrimaryButton, Stack } from '@fluentui/react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { TestBladeHeader } from 'src/luis/projects/testPane/components/common/TestBladeHeader';
import styled from 'styled-components';

export type FilterItem = {
    itemName: string;
    isChecked: boolean;
};

export type CLUTestModelsFiltersBladeProps = {
    isOpen: boolean;
    onDismiss: () => void;
    entityLabelsToFilter: FilterItem[];
    entityPredictionsToFilter: FilterItem[];
    intentLabelsToFilter: FilterItem[];
    intentPredictionsToFilter: FilterItem[];
    resultTypesToFilter: FilterItem[];
    onFiltersChange: (
        entityNewLabels: FilterItem[],
        entityNewPredictions: FilterItem[],
        intentNewLabels: FilterItem[],
        intentNewPredictions: FilterItem[],
        newResults: FilterItem[]
    ) => void;
};

const messages = defineMessages({
    apply: { id: 'clu.cluTestModelsFiltersBlade.apply', defaultMessage: 'Apply' },
    search: { id: 'clu.cluTestModelsFiltersBlade.search', defaultMessage: 'Search' },
    cancel: { id: 'clu.cluTestModelsFiltersBlade.cancel', defaultMessage: 'Cancel' },
    seeAll: { id: 'clu.cluTestModelsFiltersBlade.seeAll', defaultMessage: 'See all' },
    header: { id: 'clu.cluTestModelsFiltersBlade.header', defaultMessage: 'Filters' },
    reset: { id: 'clu.cluTestModelsFiltersBlade.reset', defaultMessage: 'Reset all filters' },
    resultType: { id: 'clu.cluTestModelsFiltersBlade.resultType', defaultMessage: 'Result Type' },
    allFilters: { id: 'clu.cluTestModelsFiltersBlade.allFilters', defaultMessage: 'All filters' },
    noItemsFound: { id: 'clu.cluTestModelsFiltersBlade.noItemsFound', defaultMessage: 'No {itemsType} found.' },
    entityLabeledAs: { id: 'clu.cluTestModelsFiltersBlade.entityLabeledAs', defaultMessage: 'Entity - Labeled as' },
    seeAllAriaLabel: { id: 'clu.cluTestModelsFiltersBlade.seeAllAriaLabel', defaultMessage: 'See all {itemsType}' },
    intentLabeledAs: { id: 'clu.cluTestModelsFiltersBlade.intentLabeledAs', defaultMessage: 'Intent - Labeled as' },
    intentPredictedAs: { id: 'clu.cluTestModelsFiltersBlade.intentPredictedAs', defaultMessage: 'Intent - Predicted as' },
    entityPredictedAs: { id: 'clu.cluTestModelsFiltersBlade.entityPredictedAs', defaultMessage: 'Entity - Predicted as' },
    noItemsAvailable: { id: 'clu.cluTestModelsFiltersBlade.noItemsAvailable', defaultMessage: 'No {itemsType} available.' }
});

const theme = getScopedTheme('TestModelsFiltersBlade');
const itemsLimitPerSection = 5;

const ItemsSectionsContainer = styled(Stack)({ marginBottom: '10px' });
const BackButtonContainer = styled(ActionButton)({ height: 16, color: theme.primaryColor });
const SeeAll = styled(Link)({
    fontSize: 12,
    marginTop: 8,
    color: theme.primaryColor,
    $nest: { '&:hover, &:active': { color: theme.primaryColor } }
});

export const CLUTestModelsFiltersBlade = (props: CLUTestModelsFiltersBladeProps) => {
    const {
        isOpen,
        onDismiss,
        entityLabelsToFilter,
        entityPredictionsToFilter,
        intentLabelsToFilter,
        intentPredictionsToFilter,
        resultTypesToFilter,
        onFiltersChange
    } = props;

    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const [entityFilteredLabels, updateEntityFilteredLabels] = React.useState(entityLabelsToFilter);
    const [intentFilteredLabels, updateIntentFilteredLabels] = React.useState(intentLabelsToFilter);
    const [filteredResults, updateFilteredResults] = React.useState(resultTypesToFilter);
    const [entityFilteredPrediction, updateEntityFilteredPredictions] = React.useState(entityPredictionsToFilter);
    const [intentFilteredPrediction, updateIntentFilteredPredictions] = React.useState(intentPredictionsToFilter);

    const [showAllEntityLabels, setShowAllEntityLabels] = React.useState(false);
    const [showAllIntentLabels, setShowAllIntentLabels] = React.useState(false);
    const [showAllEntityPredictions, setShowAllEntityPredictions] = React.useState(false);
    const [showAllIntentPredictions, setShowAllIntentPredictions] = React.useState(false);
    const [showAllResults, setShowAllResults] = React.useState(false);

    const [lastFocusedElement, setLastFocusedElement] = React.useState(null);

    const isOrchestrationProject = rootStore.currentCluProjectStore.projectType === 'Orchestration';

    React.useEffect(() => {
        document.getElementById(lastFocusedElement)?.focus();
    }, [lastFocusedElement]);

    React.useEffect(() => {
        updateEntityFilteredLabels(entityLabelsToFilter);
    }, [entityLabelsToFilter]);

    React.useEffect(() => {
        updateIntentFilteredLabels(intentLabelsToFilter);
    }, [intentLabelsToFilter]);

    React.useEffect(() => {
        updateFilteredResults(resultTypesToFilter);
    }, [resultTypesToFilter]);

    React.useEffect(() => {
        updateEntityFilteredPredictions(entityPredictionsToFilter);
    }, [entityPredictionsToFilter]);

    React.useEffect(() => {
        updateIntentFilteredPredictions(intentPredictionsToFilter);
    }, [intentPredictionsToFilter]);

    const ItemsSection = ({
        items,
        title,
        isHidden,
        itemsType,
        toggleFilter,
        onSeeAllClick,
        isSearchHidden
    }: {
        title: string;
        isHidden: boolean;
        itemsType: string;
        items: FilterItem[];
        isSearchHidden: boolean;
        onSeeAllClick: () => void;
        toggleFilter: (item: FilterItem) => void;
    }) => {
        const [query, setQuery] = React.useState('');

        const filteredItems = substringSearchStrategy(items, query, ['itemName']);
        const itemLengthToShow = isSearchHidden ? Math.min(itemsLimitPerSection, items.length) : filteredItems.length;

        if (isHidden) {
            return null;
        }

        return (
            <Stack tokens={{ childrenGap: 6, padding: '0 0 0 12px' }}>
                <LuisText variant="medium" fontWeight={600}>
                    {title}
                </LuisText>

                {items.length ? (
                    [
                        ...(!isSearchHidden
                            ? [
                                  <LuisDebouncedSearchBox
                                      value={query}
                                      onChange={(_, v) => setQuery(v)}
                                      placeholder={localize(messages.search)}
                                      debounceTime={0}
                                  />
                              ]
                            : []),
                        <SearchResultsAnnouncer isActive={query.length > 0} itemsLength={filteredItems.length} />,
                        ...(filteredItems.length
                            ? [
                                  <Stack key={title} tokens={{ childrenGap: 6 }} styles={{ root: { padding: '5px 0' } }}>
                                      {filteredItems.map(
                                          (item, index) =>
                                              index < itemLengthToShow && (
                                                  <Checkbox
                                                      label={item.itemName}
                                                      checked={item.isChecked}
                                                      ariaLabel={item.itemName}
                                                      key={`${item.itemName}-${itemsType}`}
                                                      id={`${item.itemName}-${item.isChecked}-${itemsType}`}
                                                      onChange={() => {
                                                          toggleFilter(item);
                                                          setLastFocusedElement(`${item.itemName}-${!item.isChecked}-${itemsType}`);
                                                      }}
                                                      onRenderLabel={() => (
                                                          <TruncatedText title={item.itemName} maxWidth="300px">
                                                              {item.itemName}
                                                          </TruncatedText>
                                                      )}
                                                  />
                                              )
                                      )}

                                      {isSearchHidden && items.length > itemsLimitPerSection ? (
                                          <SeeAll onClick={onSeeAllClick} aria-label={localize(messages.seeAllAriaLabel, { itemsType })}>
                                              {localize(messages.seeAll)}
                                          </SeeAll>
                                      ) : null}
                                  </Stack>
                              ]
                            : [
                                  <LuisText variant="small" styles={{ root: { margin: 'auto' } }}>
                                      {localize(messages.noItemsFound, { itemsType })}
                                  </LuisText>
                              ])
                    ]
                ) : (
                    <LuisText variant="small" styles={{ root: { margin: 'auto' } }}>
                        {localize(messages.noItemsAvailable, { itemsType })}
                    </LuisText>
                )}
            </Stack>
        );
    };

    const handleToggleEntityLabelItem = (item: FilterItem) => {
        updateEntityFilteredLabels(entityFilteredLabels.map(i => (i.itemName === item.itemName ? { ...i, isChecked: !i.isChecked } : i)));
    };

    const handleToggleIntentLabelItem = (item: FilterItem) => {
        updateIntentFilteredLabels(intentFilteredLabels.map(i => (i.itemName === item.itemName ? { ...i, isChecked: !i.isChecked } : i)));
    };

    const handleToggleResultItem = (item: FilterItem) => {
        updateFilteredResults(filteredResults.map(i => (i.itemName === item.itemName ? { ...i, isChecked: !i.isChecked } : i)));
    };

    const handleToggleEntityPredictionItem = (item: FilterItem) => {
        updateEntityFilteredPredictions(
            entityFilteredPrediction.map(i => (i.itemName === item.itemName ? { ...i, isChecked: !i.isChecked } : i))
        );
    };

    const handleToggleIntentPredictionItem = (item: FilterItem) => {
        updateIntentFilteredPredictions(
            intentFilteredPrediction.map(i => (i.itemName === item.itemName ? { ...i, isChecked: !i.isChecked } : i))
        );
    };

    const showAllSections = () => {
        setShowAllEntityLabels(false);
        setShowAllIntentLabels(false);
        setShowAllEntityPredictions(false);
        setShowAllIntentPredictions(false);
        setShowAllResults(false);
    };

    const resetAllFilters = () => {
        updateEntityFilteredLabels(entityFilteredLabels.map(i => ({ ...i, isChecked: false })));
        updateIntentFilteredLabels(intentFilteredLabels.map(i => ({ ...i, isChecked: false })));
        updateFilteredResults(filteredResults.map(i => ({ ...i, isChecked: false })));
        updateEntityFilteredPredictions(entityFilteredPrediction.map(i => ({ ...i, isChecked: false })));
        updateIntentFilteredPredictions(intentFilteredPrediction.map(i => ({ ...i, isChecked: false })));
    };

    const isThisSectionHidden = (isShown: boolean) =>
        (showAllEntityLabels || showAllEntityPredictions || showAllIntentLabels || showAllIntentPredictions || showAllResults) && !isShown;

    const BladeContent = (
        <Stack verticalAlign="space-between" verticalFill styles={{ root: { overflowY: 'auto' } }}>
            <ItemsSectionsContainer tokens={{ childrenGap: 16, padding: '12px 0 0 0' }}>
                {(showAllEntityLabels || showAllEntityPredictions || showAllIntentLabels || showAllIntentPredictions || showAllResults) && (
                    <BackButtonContainer onClick={showAllSections} primary>
                        <Stack tokens={{ childrenGap: 4 }} verticalAlign="center" horizontal>
                            <Icon iconName="Back" />
                            <span>{localize(messages.allFilters)}</span>
                        </Stack>
                    </BackButtonContainer>
                )}

                <ItemsSection
                    itemsType="intentLabels"
                    items={intentFilteredLabels}
                    isSearchHidden={!showAllIntentLabels}
                    toggleFilter={handleToggleIntentLabelItem}
                    title={localize(messages.intentLabeledAs)}
                    onSeeAllClick={() => setShowAllIntentLabels(true)}
                    isHidden={isThisSectionHidden(showAllIntentLabels)}
                />
                <ItemsSection
                    itemsType="intentPredictions"
                    items={intentFilteredPrediction}
                    isSearchHidden={!showAllIntentPredictions}
                    title={localize(messages.intentPredictedAs)}
                    toggleFilter={handleToggleIntentPredictionItem}
                    isHidden={isThisSectionHidden(showAllIntentPredictions)}
                    onSeeAllClick={() => setShowAllIntentPredictions(true)}
                />
                {!isOrchestrationProject && (
                    <>
                        <ItemsSection
                            itemsType="entityLabels"
                            items={entityFilteredLabels}
                            isSearchHidden={!showAllEntityLabels}
                            toggleFilter={handleToggleEntityLabelItem}
                            title={localize(messages.entityLabeledAs)}
                            onSeeAllClick={() => setShowAllEntityLabels(true)}
                            isHidden={isThisSectionHidden(showAllEntityLabels)}
                        />
                        <ItemsSection
                            itemsType="entityPredictions"
                            items={entityFilteredPrediction}
                            isSearchHidden={!showAllEntityPredictions}
                            title={localize(messages.entityPredictedAs)}
                            toggleFilter={handleToggleEntityPredictionItem}
                            isHidden={isThisSectionHidden(showAllEntityPredictions)}
                            onSeeAllClick={() => setShowAllEntityPredictions(true)}
                        />
                    </>
                )}
                <ItemsSection
                    itemsType="results"
                    items={filteredResults}
                    isSearchHidden={!showAllResults}
                    toggleFilter={handleToggleResultItem}
                    title={localize(messages.resultType)}
                    onSeeAllClick={() => setShowAllResults(true)}
                    isHidden={isThisSectionHidden(showAllResults)}
                />
            </ItemsSectionsContainer>
            <Stack horizontal horizontalAlign="space-between">
                <Stack horizontal tokens={{ childrenGap: 6 }}>
                    <PrimaryButton
                        onClick={() => {
                            onFiltersChange(
                                entityFilteredLabels,
                                entityFilteredPrediction,
                                intentFilteredLabels,
                                intentFilteredPrediction,
                                filteredResults
                            );
                            onDismiss();
                        }}>
                        {localize(messages.apply)}
                    </PrimaryButton>
                    <DefaultButton onClick={onDismiss}>{localize(messages.cancel)}</DefaultButton>
                </Stack>
                <Link onClick={resetAllFilters}>{localize(messages.reset)}</Link>
            </Stack>
        </Stack>
    );

    const blades = [
        {
            key: 'filtersBladeId',
            header: <TestBladeHeader>{localize(messages.header)}</TestBladeHeader>,
            content: BladeContent
        }
    ];

    return (
        <StackBladeHost
            isOpen={isOpen}
            blades={blades}
            onDismiss={onDismiss}
            topSpacing={theme.topSpacing}
            onLastBladeDismissed={onDismiss}
        />
    );
};
